import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Form, message, Space, Col, Typography } from "antd";
import { RedoOutlined, BorderOutlined, DeleteOutlined, FileSearchOutlined, CloseOutlined, CaretRightOutlined, CheckOutlined } from "@ant-design/icons";
import EditIcon from "../../../ui/EditIcon";
import "../../ipforms.css";
import NurseData from "./NurseData";
import ReviewData from "./ReviewData";
import useQuery from "../../../../hooks/useQuery";
import CustomCard from "../../../../blocks/Cards/CustomCard";
import {
  createOrderSheetApi,
  updateOrderSheetApi,
} from "../../../../services/orderSheet.services";
import dayjs from "dayjs";
import { UiButton, UiTag } from "../../../ui";
const { Text } = Typography;

function CareItemForm(props, ref) {
  const {
    getData,
    formData,
    formType,
    formItems,
    formState,
    iaAction,
    setFormState,
    isIaCareItem,
    reviewData,
    reviewFormItems,
    updateIACareItemRecord,
  } = props;

  const query = useQuery();
  const [form] = Form.useForm();
  // This should be below of useForm()
  useImperativeHandle(ref, () => ({ onFinish }));
  const [loading, setLoading] = useState(false);
  const orderDate = dayjs(formData?.order_date).format("DD-MM-YYYY h:mm A");
  const completedDate = dayjs(formData?.completed_date).format(
    "DD-MM-YYYY HH:mm A"
  );
  const [nurseModalStatus, setNurseModalStatus] = useState(false);
  const [reviewModalStatus, setReviewModalStatus] = useState(false);
  const [timer, setTimer] = useState(null);
  const isActive = query?.status === "current";

  useEffect(() => {
    const updatedAt = dayjs(formData?.order_date);
    const now = dayjs();
    const secondsElapsed = now.diff(updatedAt, "second");
    const secondsLeft = Math.max(60 - secondsElapsed, 0);

    if (secondsLeft > 0) {
      setTimer(secondsLeft);

      const intervalId = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(intervalId);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [formData?.order_date]);

  const showEditButton =
    timer > 0 &&
    !(
      formType === "ostomy_care" ||
      formType === "eye_care_eye_patching_guard" ||
      formType === "eye_care_trasorrhaphy"
    );

  useEffect(() => {
    if (!formData) return;

    const nestedData =
      formData?.data &&
      typeof formData.data === "object" &&
      !Array.isArray(formData.data)
        ? formData.data
        : null;

    const values = nestedData || formData;

    const applyValues = () => {
      form.setFieldsValue({
        ...values,
        // Prefer nested care-item payload; fall back to legacy IA field name
        tracheostomy:
          values?.tracheostomy ??
          nestedData?.tracheostomy ??
          formData?.tracheostomy_type ??
          formData?.tracheostomy,
        tracheostomy_size:
          values?.tracheostomy_size ??
          nestedData?.tracheostomy_size ??
          formData?.tracheostomy_size,
        decannulation_trial:
          values?.decannulation_trial ??
          nestedData?.decannulation_trial ??
          formData?.decannulation_trial,
      });
    };

    applyValues();
    // Re-apply after edit mode mounts Radio/Input fields (replacing Descriptions)
    const frameId = window.requestAnimationFrame(applyValues);
    return () => window.cancelAnimationFrame(frameId);
  }, [formData, formState?.editingIndex, form]);

  async function updateAction(action) {
    setLoading(true);
    try {
      const response = await updateOrderSheetApi(
        {
          action,
          care_type: formType,
          admission_id: query?.patient,
        },
        formData?.id
      );

      await getData();
      message.success(response.data.message);
    } catch (error) {
      console.log(error);
      message.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }

  async function onFinish(values, iaFormType = null) {
    if (formType === "ostomy_care") {
      values = { ostomy_care: "yes" }; // Override values for ostomy_care
    }
    if (formType === "eye_care_trasorrhaphy") {
      values = { tarsorrhaphy: "yes" }; // Override values for ostomy_care
    }
    if (formType === "eye_care_eye_patching_guard") {
      values = { eye_guard: "yes" }; // Override values for ostomy_care
    }

    setLoading(true);
    try {
      const response =
        (!formState?.editingIndex && (formState?.adding || true)) ||
        isIaCareItem
          ? await createOrderSheetApi({
              data: values,
              admission_id: query?.patient,
              care_type: isIaCareItem ? iaFormType : formType,
              action: "start",
            })
          : await updateOrderSheetApi(
              {
                data: values,
                care_type: formType,
                admission_id: query?.patient,
              },
              formData?.id
            );
      if (typeof getData === "function") await getData();
      if (typeof setFormState === "function")
        setFormState({ adding: false, editingIndex: null });
      message.success(response.data.message);
    } catch (error) {
      console.log(error);
      message.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }

  const getActionVariant = (label) => {
    if (
      label === "Start" ||
      (isIaCareItem &&
        iaAction?.[formType]?.status === "continue" &&
        label === "Continue")
    ) {
      return "primary";
    }
    if (label === "Done") return "primary";
    if (label === "Discard" || label === "Cancel") return "secondary";
    return "ghost";
  };

  const renderActionButton = (icon, label, onClick, disabled = false) =>
    isActive && (
      <UiButton
        size="md"
        variant={getActionVariant(label)}
        className={`order-action-btn ${
          ["Stop", "Delete"].includes(label) ? "order-action-btn-danger" : ""
        }`}
        onClick={onClick}
        loading={disabled}
      >
        <Space size={6} className="icon-text-segment order-action-content">
          {icon}
          <span className="order-action-label">{label}</span>
        </Space>
      </UiButton>
    );

  const actions = [];

  if (formState?.adding && !formData) {
    // Delete Action while creating new careitem
    actions.push(
      renderActionButton(
        <DeleteOutlined style={{ color: "red", fontSize: "12px" }} />,
        "Delete",
        () => setFormState((prev) => ({ ...prev, adding: false }))
      )
    );
  }

  if (formData && !isIaCareItem) {
    if (formData?.action !== "stop") {
      if (formState?.editingIndex !== formData?.id) {
        // Edit Action to update
        showEditButton &&
          actions.push(
            renderActionButton(
              <>
                <EditIcon style={{ fontSize: "14px" }} />
                <Text type="secondary">{timer}s</Text>
              </>,

              "Edit",
              () =>
                setFormState((prev) => ({
                  ...prev,
                  editingIndex: formData?.id,
                }))
            )
          );
      } else {
        // Submit Action while update
        actions.push(
          renderActionButton(
            <CheckOutlined style={{ fontSize: "14px" }} />,
            "Done",
            () => form.submit(),
            loading
          )
        );

        // Cancel Action while update
        actions.push(
          renderActionButton(
            <CloseOutlined style={{ fontSize: "14px" }} />,
            "Cancel",
            () =>
              setFormState((prev) => ({
                ...prev,
                editingIndex: null,
              })),
            loading
          )
        );
      }
    }
  } else if (isIaCareItem) {
    // Discard Action from Initial Assessment
    actions.push(
      renderActionButton(<CloseOutlined />, "Discard", () =>
        updateIACareItemRecord({
          [formType]: {
            status: "discard",
            data: form?.getFieldsValue(),
          },
        })
      )
    );

    // Continue Action from Initial Assessment
    actions.push(
      renderActionButton(
        <CaretRightOutlined />,
        "Continue",
        () =>
          updateIACareItemRecord({
            [formType]: {
              status: "continue",
              data: form?.getFieldsValue(),
            },
          }),
        loading
      )
    );
  } else {
    // Form submit action
    actions.push(
      renderActionButton(
        <CaretRightOutlined style={{ fontSize: "14px" }} />,
        "Start",
        () => form.submit(),
        loading
      )
    );
  }

  if (formData && !isIaCareItem) {
    // Stop Action
    actions.push(
      formData?.action !== "stop" &&
        formState?.editingIndex !== formData?.id &&
        renderActionButton(
          <BorderOutlined style={{ color: "red", fontSize: "12px" }} />,
          "Stop",
          () => updateAction("stop"),
          loading
        )
    );

    // Replace Action
    actions.push(
      ["feeding_tube", "urinary_catheter_care"]?.includes(formType) &&
        formData?.action !== "stop" &&
        renderActionButton(
          <RedoOutlined style={{ color: "orange", fontSize: "12px" }} />,
          "Replace",
          () => updateAction("replace"),
          loading
        )
    );

    // Nurse Review Data
    // actions.push(
    //   renderActionButton(
    //     <Image
    //       preview={false}
    //       src={nurse}
    //       style={{
    //         height: "14px",
    //         width: "auto",
    //         marginBottom: "4px",
    //         marginRight: "4px",
    //       }}
    //     />,
    //     "Nurse",
    //     () => setNurseModalStatus(true)
    //   )
    // );

    // Daily Review Data
    actions.push(
      ["feeding_tube", "ostomy_care"].includes(formType) &&
        renderActionButton(
          <FileSearchOutlined style={{ fontSize: "14px" }} />,
          "Review",
          () => setReviewModalStatus(true)
        )
    );
  }

  const actionButtons = actions.filter((item) => item);

  return (
    <Col span={24} className="order-sheet-form-wrapper">
      <Form
        form={form}
        layout="vertical"
        name={formType}
        className="order-care-form"
        onFinish={onFinish}
      >
        <CustomCard
          size="small"
          className={`order-care-card ${
            formData?.action === "stop" ? "care-item-card-stop-styles" : ""
          }`}
          extra={
            <Space className="order-care-card-extra" wrap size={[6, 6]}>
              {formData?.completed_date && (
                <UiTag tone="danger" size="sm">
                  Stopped on {completedDate}
                </UiTag>
              )}
              {formData?.submitted_by && (
                <UiTag tone="info" size="sm">
                  Advised by {formData?.submitted_by?.first_name || "-"}
                  {" on "}
                  {orderDate}
                </UiTag>
              )}
            </Space>
          }
        >
          {formItems}
          {actionButtons.length > 0 && (
            <div className="order-sheet-action-row">{actionButtons}</div>
          )}
        </CustomCard>
      </Form>
      {reviewModalStatus && (
        <ReviewData
          data={formData?.review_data}
          formType={formType}
          getData={getData}
          formData={formData}
          reviewFormItems={reviewFormItems}
          reviewModalStatus={reviewModalStatus}
          setReviewModalStatus={setReviewModalStatus}
        />
      )}
      {nurseModalStatus && (
        <NurseData
          id={formData?.id}
          data={[]}
          nurseModalStatus={nurseModalStatus}
          setNurseModalStatus={setNurseModalStatus}
        />
      )}
    </Col>
  );
}

export default forwardRef(CareItemForm);
